/**
 * Localized notification titles for the alert surfaces (bell popover + toast /
 * desktop pop-up).
 *
 * Notification `title`/`body` are persisted in English at emit time on the
 * backend, so the inbox would otherwise show English text to a Hebrew/Spanish
 * user. Every event already declares a translatable `i18n_key` in the shared
 * `notification_events.config.json` catalog (the same source the preferences
 * matrix renders from), so the alert surfaces re-derive a localized title from
 * the notification's `notification_type` and fall back to the stored title when
 * no catalog entry exists. Terminology placeholders (`{{Deal}}`, `{{Task}}`, …)
 * resolve automatically via the global i18next `interpolation.defaultVariables`.
 *
 * The `body` (a per-event excerpt — user content like the comment text) is NOT
 * localized; it is shown verbatim.
 */
import type { TFunction } from 'i18next';

import notificationEventsConfig from '../../shared-config/notification_events.config.json';

interface RawEvent {
  notification_type?: string;
  i18n_key?: string;
}
interface RawCategory {
  events?: RawEvent[];
}
interface RawModule {
  categories?: RawCategory[];
}
interface RawConfig {
  modules?: Record<string, RawModule>;
}

/**
 * Modules whose stored titles are intentionally dynamic — they embed per-event
 * specifics the generic catalog label would drop (e.g. messaging:
 * `"{sender} mentioned you: {preview}"`). Those titles are surfaced verbatim.
 */
const DYNAMIC_TITLE_MODULES = new Set<string>(['messaging']);

function buildTitleKeyMap(): Record<string, string> {
  const map: Record<string, string> = {};
  const ambiguous = new Set<string>();
  const modules = (notificationEventsConfig as RawConfig).modules ?? {};

  for (const [moduleId, mod] of Object.entries(modules)) {
    if (DYNAMIC_TITLE_MODULES.has(moduleId)) continue;
    for (const category of mod.categories ?? []) {
      for (const event of category.events ?? []) {
        const type = event.notification_type;
        const key = event.i18n_key;
        if (!type || !key) continue;
        if (type in map && map[type] !== key) {
          // Defensive: a notification_type that resolves to two different catalog
          // keys can't be disambiguated from the type alone, so it falls back to
          // the stored title. The first-seen value is written above and removed by
          // the `ambiguous` cleanup below. (No such cross-module collision exists
          // in the current config — `message.new`'s DM/channel split is already
          // dropped by the messaging module exclusion before reaching here.)
          ambiguous.add(type);
          continue;
        }
        map[type] = key;
      }
    }
  }

  for (const type of ambiguous) delete map[type];
  return map;
}

/** `notification_type` → catalog `i18n_key` (unambiguous, non-dynamic only). */
export const NOTIFICATION_TITLE_I18N_KEYS: Record<string, string> = buildTitleKeyMap();

/**
 * Localized title for a notification: the catalog title when the type resolves
 * to a single catalog event, otherwise the stored English title.
 */
export function localizedNotificationTitle(
  t: TFunction,
  notificationType: string | null | undefined,
  fallbackTitle: string,
): string {
  if (!notificationType) return fallbackTitle;
  const i18nKey = NOTIFICATION_TITLE_I18N_KEYS[notificationType];
  if (!i18nKey) return fallbackTitle;
  return t(i18nKey, { defaultValue: fallbackTitle });
}
